import { FieldDropdown, FieldNumber, type Block, type Input } from 'blockly/core';

import {
  DISPLAY_POSITION_LINE_COLUMN,
  DISPLAY_POSITION_XY,
  TFT_HEIGHT,
  TFT_WIDTH,
} from './tft-fields.js';

const POSITION_DETAILS_INPUT = 'POSITION_DETAILS';
const POSITION_FIELD_NAMES = ['LINE', 'COLUMN', 'X', 'Y'] as const;

type TftPositionBlock = Block & { tftPositionMode?: string };

function snapshotPositionFields(block: Block): Record<string, number> {
  const saved: Record<string, number> = {};
  for (const name of POSITION_FIELD_NAMES) {
    const value = block.getFieldValue(name);
    if (value != null && value !== '') {
      saved[name] = Number(value);
    }
  }
  return saved;
}

function removePositionDetails(block: Block): void {
  if (block.getInput(POSITION_DETAILS_INPUT)) {
    block.removeInput(POSITION_DETAILS_INPUT);
  }
}

function appendLineColumnFields(input: Input, saved: Record<string, number>): void {
  const line = new FieldNumber(saved.LINE ?? 1, 1, 20, 1);
  const column = new FieldNumber(saved.COLUMN ?? 1, 1, 21, 1);
  input.appendField('line').appendField(line, 'LINE').appendField('column').appendField(column, 'COLUMN');
}

function appendXyFields(input: Input, saved: Record<string, number>): void {
  const x = new FieldNumber(saved.X ?? 0, 0, TFT_WIDTH - 1, 1);
  const y = new FieldNumber(saved.Y ?? 0, 0, TFT_HEIGHT - 1, 1);
  input.appendField('x').appendField(x, 'X').appendField('y').appendField(y, 'Y');
}

/** Rebuilds Line/Column or X/Y for TFT text/cursor blocks. */
export function updateTftPositionDetails(
  block: Block,
  positionMode?: string,
  force = false,
): void {
  if (block.isDisposed()) {
    return;
  }

  const mode =
    positionMode ??
    String(block.getFieldValue('POSITION_MODE') ?? DISPLAY_POSITION_LINE_COLUMN);
  const resolved =
    mode === DISPLAY_POSITION_XY ? DISPLAY_POSITION_XY : DISPLAY_POSITION_LINE_COLUMN;
  const positionBlock = block as TftPositionBlock;

  if (!force && positionBlock.tftPositionMode === resolved && block.getInput(POSITION_DETAILS_INPUT)) {
    return;
  }

  const saved = snapshotPositionFields(block);
  removePositionDetails(block);

  const input = block.appendDummyInput(POSITION_DETAILS_INPUT);
  if (resolved === DISPLAY_POSITION_XY) {
    appendXyFields(input, saved);
  } else {
    appendLineColumnFields(input, saved);
  }

  positionBlock.tftPositionMode = resolved;
}

export function createTftPositionModeDropdown(block: Block): FieldDropdown {
  return new FieldDropdown(
    [
      ['Line / Column', DISPLAY_POSITION_LINE_COLUMN],
      ['X / Y', DISPLAY_POSITION_XY],
    ],
    (newValue) => {
      const resolved =
        String(newValue ?? '') === DISPLAY_POSITION_XY
          ? DISPLAY_POSITION_XY
          : DISPLAY_POSITION_LINE_COLUMN;
      queueMicrotask(() => {
        if (!block.isDisposed()) {
          updateTftPositionDetails(block, resolved, true);
        }
      });
      return resolved;
    },
  );
}
